import Button from 'components/button';
import Input from 'components/input';
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import request from 'request';
import styles from './create-modal.module.scss';

const Modal = ({ onClose }: { onClose: () => void }) => {
  const [inn, setInn] = React.useState('');
  const [interestRate, setInterestRate] = React.useState('');
  const [duration, setDuration] = React.useState('');
  const [pending, setPending] = React.useState(false);

  const [currency, setCurrency] = React.useState({
    key: '',
    value: '',
  });

  const [requestedAmount, setRequestedAmount] = React.useState('');

  const [validError, setValidError] = React.useState<{
    name: 'inn' | 'interestRate' | 'duration' | 'currency' | 'requestedAmount';
    value: string;
  } | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    if (!requestedAmount.trim() || !currency.key) return;

    const amount = Number(requestedAmount);
    const maxAmount = currency.key === 'usd' ? 35000 : currency.key === 'tjs' ? 350000 : amount;
    setRequestedAmount(String(Math.min(amount, maxAmount)));

    if (!interestRate.trim()) return;

    const rate = Number(interestRate);
    const minRate = currency.key === 'usd' ? 12 : 20;
    const maxRate = currency.key === 'usd' ? 14 : 22;
    setInterestRate(String(Math.max(minRate, Math.min(rate, maxRate))));
  }, [currency.key]);

  const handleCreate = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (inn.trim().length !== 9) {
      return setValidError({
        name: 'inn',
        value: 'ИНН должен состоять из 9 символов',
      });
    } else if (interestRate.trim().length === 0) {
      return setValidError({
        name: 'interestRate',
        value: 'Процентная ставка обязательна для заполнения',
      });
    } else if (duration.trim().length === 0) {
      return setValidError({
        name: 'duration',
        value: 'Длительность обязательна для заполнения',
      });
    } else if (currency.key.trim().length === 0) {
      return setValidError({
        name: 'currency',
        value: 'Валюта кредита обязательна для заполнения',
      });
    } else if (requestedAmount.trim().length === 0) {
      return setValidError({
        name: 'requestedAmount',
        value: 'Запрашиваемая сумма обязательна для заполнения',
      });
    } else setValidError(null);
    setPending(true);

    request
      .post('/applications_gold', {
        inn,
        int_rate: interestRate,
        duration,
        currency: currency.key,
        requested_amount: requestedAmount,
      })
      .then((res) => res.data)
      .then((data) => {
        navigate(`/application/collateral/${data.id}/scoring`);
      })
      .finally(() => {
        onClose();
        setPending(false);
      });
  };

  return (
    <div className={styles.modal} onMouseDown={onClose}>
      <form
        className={styles.modalWrap}
        onMouseDown={(event) => event.stopPropagation()}
        onSubmit={handleCreate}
      >
        <div className={styles.title}>Новая заявка</div>
        <div className={styles.inputs}>
          <div className={styles.input}>
            <Input
              title="ИНН"
              type="string"
              value={inn}
              onInput={(value) => {
                if (/^[0-9]*$/.test(value) && value.length <= 9) {
                  setInn(value);
                }
              }}
              placeholder={'ИНН клиента'}
              error={validError?.name === 'inn' ? validError.value : ''}
            />
          </div>

          <div className={styles.input}>
            <Input
              title="Запрашиваемая сумма"
              type="integer"
              value={requestedAmount}
              onInput={(value) => setRequestedAmount(value)}
              step={1}
              min={1}
              max={currency.key === 'usd' ? 35000 : 350000}
              placeholder={`макс: ${currency.key === 'usd' ? 35000 : 350000}`}
              unit={currency.key === '' ? '' : currency.key === 'tjs' ? 'TJS' : 'USD'}
              error={validError?.name === 'requestedAmount' ? validError.value : ''}
            />
          </div>

          <div className={styles.input}>
            <Input
              title="Валюта кредита"
              type="select"
              value={currency.key}
              onSelect={(value) => setCurrency(value)}
              options={[
                { key: 'tjs', value: 'TJS' },
                { key: 'usd', value: 'USD' },
              ]}
              error={validError?.name === 'currency' ? validError.value : ''}
            />
          </div>

          <div className={styles.input}>
            <Input
              title="Длительность"
              type="float"
              value={duration}
              onInput={(value) => setDuration(value)}
              step={1}
              min={3}
              max={36}
              placeholder={'мин: 3 макс: 36'}
              unit="мес."
              error={validError?.name === 'duration' ? validError.value : ''}
            />
          </div>

          <div className={styles.input}>
            <Input
              title="Процентная ставка"
              type="float"
              value={interestRate}
              onInput={(value) => setInterestRate(value)}
              step={1}
              min={currency.key === 'usd' ? 12 : currency.key === '' ? 20 : 20}
              max={currency.key === 'usd' ? 14 : currency.key === '' ? 22 : 22}
              placeholder={`мин: ${
                currency.key === 'usd' ? 12 : currency.key === '' ? 20 : 20
              } макс: ${currency.key === 'usd' ? 14 : currency.key === '' ? 22 : 22}`}
              unit="%"
              error={validError?.name === 'interestRate' ? validError.value : ''}
            />
          </div>
        </div>
        <div className={styles.buttonGroup}>
          <Button color="orange" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" color="green" pending={pending}>
            Создать
          </Button>
        </div>
      </form>
    </div>
  );
};

export default Modal;
